import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

const STORAGE_PREFIX = 'kintsugi.agent.persistedThread.v1:';
/** Pre-rename prefix — migrated once per scope when the new key is missing (see ``readOrCreateFromSession``). */
const LEGACY_STORAGE_PREFIX = 'kintsugi.advisor.persistedThread.v1:';
/** Stores the thread UUID known to exist server-side for this scope (same value as the persisted id). */
const SERVER_READY_STORAGE_PREFIX = 'kintsugi.agent.persistedThreadServerReady.v1:';

function storageKey(scopeKey: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(scopeKey)}`;
}

function legacyStorageKey(scopeKey: string): string {
  return `${LEGACY_STORAGE_PREFIX}${encodeURIComponent(scopeKey)}`;
}

function serverReadyStorageKey(scopeKey: string): string {
  return `${SERVER_READY_STORAGE_PREFIX}${encodeURIComponent(scopeKey)}`;
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

function readServerReadyFromSession(scopeKey: string, threadId: string): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }
  try {
    const stored = sessionStorage.getItem(serverReadyStorageKey(scopeKey));
    return Boolean(stored && isUuid(stored) && stored === threadId);
  } catch {
    return false;
  }
}

function writeServerReadySession(scopeKey: string, threadId: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(serverReadyStorageKey(scopeKey), threadId);
  } catch {
    // Quota / privacy — keep in-memory ready flag only
  }
}

function clearServerReadySession(scopeKey: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(serverReadyStorageKey(scopeKey));
  } catch {
    // Ignore storage failures
  }
}

function readOrCreateFromSession(scopeKey: string): string {
  if (typeof sessionStorage === 'undefined') {
    return crypto.randomUUID();
  }
  const key = storageKey(scopeKey);
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && isUuid(existing)) {
      return existing;
    }
    const legacyKey = legacyStorageKey(scopeKey);
    const legacy = sessionStorage.getItem(legacyKey);
    if (legacy && isUuid(legacy)) {
      try {
        sessionStorage.setItem(key, legacy);
        sessionStorage.removeItem(legacyKey);
      } catch {
        // Quota / privacy — still return migrated id in memory for this read path
      }
      return legacy;
    }
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

function writeSession(scopeKey: string, threadId: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(storageKey(scopeKey), threadId);
  } catch {
    // Quota / privacy / SecurityError — keep in-memory thread id only
  }
}

function clearSession(scopeKey: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(storageKey(scopeKey));
    sessionStorage.removeItem(legacyStorageKey(scopeKey));
    sessionStorage.removeItem(serverReadyStorageKey(scopeKey));
  } catch {
    // Ignore storage failures
  }
}

/**
 * Stable UUID for agent Postgres ``thread_id`` (sent on ``POST /v2/chat/stream`` for every lane, e.g. products, monitoring).
 * - One persisted thread per ``scopeKey`` (e.g. ``monitoring-<orgId>``) for the browser session.
 * - ``beginNewAgentThread`` clears that scope and issues a new UUID so the next turn starts a fresh DB thread.
 * - ``selectPersistedThread`` switches to a thread returned from history (``GET /v2/chat/threads``).
 * - ``threadServerReady`` is true only after the UUID is known to exist server-side (history select,
 *   successful stream settle, or ``sessionStorage`` restore of that ready flag). Virgin client-minted
 *   ids stay false so pending-report polls do not ``GET …/messages`` before the first stream.
 */
export function useAgentPersistedThreadId(scopeKey: string): {
  threadId: string;
  threadIdRef: MutableRefObject<string>;
  threadServerReady: boolean;
  markThreadServerReady: () => void;
  clearThreadServerReady: () => void;
  beginNewAgentThread: () => void;
  selectPersistedThread: (threadId: string) => void;
} {
  const [threadId, setThreadId] = useState(() => readOrCreateFromSession(scopeKey));
  const [threadServerReady, setThreadServerReady] = useState(() =>
    readServerReadyFromSession(scopeKey, threadId)
  );
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  useEffect(() => {
    const next = readOrCreateFromSession(scopeKey);
    setThreadId(next);
    threadIdRef.current = next;
    setThreadServerReady(readServerReadyFromSession(scopeKey, next));
  }, [scopeKey]);

  const markThreadServerReady = useCallback(() => {
    const current = threadIdRef.current;
    writeServerReadySession(scopeKey, current);
    setThreadServerReady(true);
  }, [scopeKey]);

  const clearThreadServerReady = useCallback(() => {
    clearServerReadySession(scopeKey);
    setThreadServerReady(false);
  }, [scopeKey]);

  const beginNewAgentThread = useCallback(() => {
    clearSession(scopeKey);
    const next = crypto.randomUUID();
    writeSession(scopeKey, next);
    threadIdRef.current = next;
    setThreadId(next);
    setThreadServerReady(false);
  }, [scopeKey]);

  const selectPersistedThread = useCallback(
    (nextId: string) => {
      const trimmed = nextId.trim();
      if (!isUuid(trimmed)) {
        return;
      }
      writeSession(scopeKey, trimmed);
      writeServerReadySession(scopeKey, trimmed);
      threadIdRef.current = trimmed;
      setThreadId(trimmed);
      setThreadServerReady(true);
    },
    [scopeKey]
  );

  return {
    threadId,
    threadIdRef,
    threadServerReady,
    markThreadServerReady,
    clearThreadServerReady,
    beginNewAgentThread,
    selectPersistedThread,
  };
}

/**
 * @deprecated Use {@link useAgentPersistedThreadId}. Same behavior; ``beginNewAdvisorThread`` is an alias of ``beginNewAgentThread``.
 */
export function useAdvisorPersistedThreadId(scopeKey: string): {
  threadId: string;
  threadIdRef: MutableRefObject<string>;
  threadServerReady: boolean;
  markThreadServerReady: () => void;
  clearThreadServerReady: () => void;
  beginNewAdvisorThread: () => void;
  selectPersistedThread: (threadId: string) => void;
} {
  const r = useAgentPersistedThreadId(scopeKey);
  return {
    threadId: r.threadId,
    threadIdRef: r.threadIdRef,
    threadServerReady: r.threadServerReady,
    markThreadServerReady: r.markThreadServerReady,
    clearThreadServerReady: r.clearThreadServerReady,
    beginNewAdvisorThread: r.beginNewAgentThread,
    selectPersistedThread: r.selectPersistedThread,
  };
}
